import React, { useCallback, useEffect, useState } from "react";
import { Button, Heading, Text, Img, SelectBox, Input } from "..";
import { default as ModalProvider } from "react-modal";
import { formatAmount } from "helper/utils";
import { listDataOnboard } from "services/onboard";

export default function DetailsModal({
  id,
  idGroup,
  isOpen,
  submitError,
  totalLoan,
  marhunValue,
  outstandingBalance,
  financingAmt,
  ProfitAmt,
  onSubmitButtonClick,
  onButtonClick,
  ...props
}) {
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState();
  const [poolValue, setPoolValue] = useState();
  const [ocValue, setOCValue] = useState();
  const [indicativeReturn, setIndicativeReturn] = useState();

  const _updateOcValue = useCallback((val) => {
    setOCValue(val)
    setPoolValue(Math.round(financingAmt + (val * financingAmt * .01)))
  }, [setOCValue, setPoolValue, financingAmt])

  const _updatePoolValue = useCallback((val) => {
    setPoolValue(val)
    setOCValue(Math.round((val - financingAmt) / financingAmt * 100))
  }, [setOCValue, setPoolValue, financingAmt])

  const handleButtonClick = () => {
    if (onButtonClick) {
      onButtonClick();
    }
  };
  const handleSubmitButtonClick = useCallback(() => {
    if (onSubmitButtonClick) {
      onSubmitButtonClick(selectedCompany?.value ?? '', poolValue, ocValue, indicativeReturn);
    }
  }, [selectedCompany, poolValue, ocValue, indicativeReturn]);

  const _fetchCompanies = useCallback(async () => {
    if (!companies.length) {
      try {
        const fetch = await listDataOnboard();
        setCompanies(
          [
            { label: 'None', value: '' },
            ...(fetch.data.companies?.filter(c => c.status === 'active')?.map((c) => ({ label: c.name, value: c.id })) ?? [])
          ]
        );
      } catch (error) {
        throw error;
      } finally {
        setLoading(false);
      }
    }
  }, [companies]);

  useEffect(() => {
    _fetchCompanies();
  }, []);

  return (
    <ModalProvider
      {...props}
      style={{
        overlay: {
          backgroundColor: isOpen ? "#D9D9D9CC" : "transparent",
        },
        content: {
          outline: "none",
        },
      }}
      appElement={document.getElementById("root")}
      isOpen={isOpen}
      className="w-[50%]"
    >
      <div className="flex flex-col items-start justify-center w-full px-10 py-10 gap-5 bg-white-A700 rounded-[10px]">
        <Text
          size="xl"
          as="p"
          className="text-center !text-blue_gray-700 !font-bold"
        >
          Are you sure you want to submit new batch with the following detail ?
        </Text>
        <div className="grid grid-cols-3 gap-y-5 gap-x-10">
          <div className="flex flex-col items-start justify-start gap-[9px]">
            <Text size="xl" as="p" className="!text-blue_gray-700">
              Total Loan
            </Text>
            <Heading as="h2" className="!text-base">
              {totalLoan}
            </Heading>
          </div>
          <div className="flex flex-col items-start justify-start gap-[9px]">
            <Text size="xl" as="p" className="!text-blue_gray-700">
              Total Marhun Value
            </Text>
            <Heading as="h2" className="!text-base">
              {formatAmount(marhunValue)}
            </Heading>
          </div>
          <div className="flex flex-col items-start justify-start gap-[9px]">
            <Text size="xl" as="p" className="!text-blue_gray-700">
              Total Outstanding Balance
            </Text>
            <Heading as="h2" className="!text-base">
              {formatAmount(outstandingBalance)}
            </Heading>
          </div>
          <div className="flex flex-col items-start justify-start gap-[9px]">
            <Text size="xl" as="p" className="!text-blue_gray-700">
              Total Financing Amount
            </Text>
            <Heading as="h2" className="!text-base">
              {formatAmount(financingAmt)}
            </Heading>
          </div>
          <div className="flex flex-col items-start justify-start gap-[9px]">
            <Text size="xl" as="p" className="!text-blue_gray-700">
              Total Profit Amount
            </Text>
            <Heading as="h2" className="!text-base">
              {formatAmount(ProfitAmt)}
            </Heading>
          </div>
          <div className="col-span-full grid grid-cols-2 gap-10">
            <div className="flex flex-col items-stretch justify-start gap-[9px]">
              <Text size="xl" as="p" className="!text-blue_gray-700">
                OC
              </Text>
              <Input
                placeholder="Input Pool Value"
                value={ocValue}
                onChange={_updateOcValue}
                type="number"
                className="gap-px border-gray-300 border-2 border-solid !rounded-[5px]"
                suffix={<Text size="xl" className="!text-blue_gray-700">%</Text>}
              />
            </div>
            <div className="flex flex-col items-stretch justify-start gap-[9px]">
              <Text size="xl" as="p" className="!text-blue_gray-700">
                Pool Value
              </Text>
              <Input
                placeholder="Input Pool Value"
                value={poolValue}
                onChange={_updatePoolValue}
                type="number"
                className="gap-px border-gray-300 border-2 border-solid !rounded-[5px]"
              />
            </div>
          </div>
          <div className="col-span-full grid grid-cols-2 gap-10">
            <div className="flex flex-col items-stretch justify-start gap-[9px]">
              <Text size="xl" as="p" className="!text-blue_gray-700">
                Issuer
              </Text>
              <SelectBox
                indicator={
                  <Img
                    src="images/img_arrowdown_blue_gray_900_02.svg"
                    alt="arrow_down"
                  />
                }
                name="company"
                placeholder="Issuer"
                options={companies}
                onChange={(value) => setSelectedCompany(value)}
                className="gap-px !text-blue_gray-100_02 border-gray-300 border-2 border-solid !rounded-[5px]"
              />
            </div>
            <div className="flex flex-col items-stretch justify-start gap-[9px]">
              <Text size="xl" as="p" className="!text-blue_gray-700">
                Indicative Return
              </Text>
              <Input
                placeholder="Input Indicative Return"
                value={indicativeReturn}
                onChange={setIndicativeReturn}
                type="number"
                className="gap-px border-gray-300 border-2 border-solid !rounded-[5px]"
              />
            </div>
          </div>
        </div>

        {submitError && (
          <Text as="p" className="!text-red-A700 ">
            {submitError}
          </Text>
        )}
        <div className="flex justify-between w-full mt-3">
          <Button
            onClick={handleSubmitButtonClick}
            disable={loading}
            color="blue_900"
            size="md"
            className="uppercase !text-xs font-bold w-fit !rounded-xl"
          >
            Submit
          </Button>
          <Button
            onClick={handleButtonClick}
            color="gray_100_01"
            size="md"
            className="uppercase !text-xs font-bold w-fit !rounded-xl"
          >
            Cancel
          </Button>
        </div>
      </div>
    </ModalProvider>
  );
}
